import { Button, FormField, Heading, IconButton, Input, Loader, SidePanel, Text } from '@wix/design-system'
import React, { FormEvent, useEffect } from 'react'
import { useInstanceContext } from '../context/InstanceContext'
import { Campaign, CampaignStatus } from '../models/shared/Campaign'
import { useFirestore } from 'reactfire'
import { addDoc, collection, doc, setDoc } from 'firebase/firestore'
import { Collections, DEFAULT_CAMPAIGN_DATA } from '../utils/shared/constants'
import { useToast } from '../context/ToastContext'
import Preview from './Preview'
import { Refresh } from '@wix/wix-ui-icons-common'
import { dashboard } from "@wix/dashboard";
import { createClient } from "@wix/sdk";

const client = createClient({
    host: dashboard.host(),
    auth: dashboard.auth(),
    modules: {
        dashboard,
    },
});

interface Props {
    show: boolean
    comapaign: Campaign | null
    onClose: () => void
    deactivateActiveCampaigns: () => Promise<void>
}

type Inputs = {
    name: string,
    labelNps: string,
    labelComment: string,
    labelThankYou: string,
    labelSubmit: string,
    logoUrl: string,
};

function CampaignSidePanel(props: Props) {
    const { instanceId } = useInstanceContext();
    const [inputs, setInputs] = React.useState<Inputs>(DEFAULT_CAMPAIGN_DATA);
    const [processing, setProcessing] = React.useState(false);
    const firestore = useFirestore();
    const { toastError, toastSuccess } = useToast();
    const [previewRenderCounter, setPreviewRenderCounter] = React.useState(0);

    useEffect(() => {
        if (props.comapaign) {
            setInputs({
                name: props.comapaign.name,
                labelNps: props.comapaign.labelNps,
                labelComment: props.comapaign.labelComment,
                labelThankYou: props.comapaign.labelThankYou,
                labelSubmit: props.comapaign.labelSubmit || '',
                logoUrl: props.comapaign.logoUrl || '',
            })
        } else {
            setInputs(DEFAULT_CAMPAIGN_DATA)
        }
    }, [props.comapaign])

    const isActive = props.comapaign?.status === 'active';

    const handleNewLogo = async () => {
        const result = await client.dashboard.openMediaManager({ category: 'IMAGE' })
        if (result?.items?.length) {
            setInputs({ ...inputs, logoUrl: result.items[0].url || '' })
        }
    }

    const saveCampaign = async (newStatus: CampaignStatus) => {
        const now = new Date();
        const newData: Partial<Campaign> = {
            instanceId,
            updatedAt: now,
            ...inputs,
            status: newStatus
        }

        try {
            if (props.comapaign) {
                await setDoc(doc(firestore, `${Collections.campaigns}/${props.comapaign.id}`), newData, { merge: true })
            } else {
                newData.createdAt = now;
                newData.responseCount = 0;

                await addDoc(collection(firestore, Collections.campaigns), newData);
            }
            toastSuccess('Campaign saved')
            props.onClose();

        } catch (error) {
            console.error('Error saving campaign', error)
            toastError('Error saving campaign');
        }
    }

    const handleSaveAndActivate = async () => {
        setProcessing(true);

        await props.deactivateActiveCampaigns();
        await saveCampaign('active');

        setProcessing(false);
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        setProcessing(true);

        await saveCampaign(props.comapaign?.status || 'inactive');

        setProcessing(false);
    }


    return <div className='sidepanel-container '
        style={{ right: props.show ? 0 : '-440px' }}
    >
        <SidePanel skin='standard' onCloseButtonClick={props.onClose} >
            <form onSubmit={handleSubmit} className='d-flex flex-column h-100' style={{ overflow: 'auto' }}>
                <SidePanel.Header title={props.comapaign ? 'Edit Campaign' : 'New Campaign'} />
                <div className='d-flex flex-column'>

                    <SidePanel.Field >

                        <FormField
                            label="Name"
                            infoContent="Name is not visible to customers and serves as a reference for you."
                        >
                            <Input value={inputs.name} onChange={e => setInputs({ ...inputs, name: e.target.value })} />
                        </FormField>
                    </SidePanel.Field>

                    <SidePanel.Field>
                        <FormField label="Logo"
                            infoContent="Optional logo to display in the feedback form">
                            <div className='d-flex align-items-center gap'>
                                {inputs.logoUrl && <img src={inputs.logoUrl} alt="Logo preview" style={{ maxHeight: '50px', maxWidth: '50px' }} />}
                                <div className='d-flex flex-column gap'>

                                    <Button type='button' size='tiny' onClick={handleNewLogo}>Upload new</Button>
                                    {inputs.logoUrl && <Button type='button' skin='inverted' size='tiny' onClick={() => setInputs({ ...inputs, logoUrl: '' })}>
                                        Remove
                                    </Button>}
                                </div>
                            </div>
                        </FormField>
                    </SidePanel.Field>
                    <SidePanel.Field>
                        <div className='d-flex flex-column gap'>
                            <FormField
                                label="Feedback question"
                                infoContent="The initial question that will be asked to customers."
                            >
                                <Input value={inputs.labelNps} onChange={e => setInputs({ ...inputs, labelNps: e.target.value })} />
                            </FormField>
                            <FormField
                                label="Comment question"
                                infoContent="The text asking for additional feedback."
                            >
                                <Input value={inputs.labelComment} onChange={e => setInputs({ ...inputs, labelComment: e.target.value })} />
                            </FormField>
                            <FormField
                                label="Comment submit text"
                                infoContent="The text displayed on the submit button."
                            >
                                <Input value={inputs.labelSubmit} onChange={e => setInputs({ ...inputs, labelSubmit: e.target.value })} />
                            </FormField>
                            <FormField
                                label="Thank you note"
                                infoContent="The message displayed after the customer submits feedback."
                            >
                                <Input value={inputs.labelThankYou} onChange={e => setInputs({ ...inputs, labelThankYou: e.target.value })} />
                            </FormField>
                        </div>
                    </SidePanel.Field>

                    <SidePanel.Field>
                        <div className='d-flex'>
                            <div className='flex-1'>
                                <Heading size='tiny'>Preview</Heading>
                                <Text size='tiny'>Responses in preview are ignored in analytics</Text>
                            </div>

                            <IconButton priority='secondary' size='small'
                                onClick={() => setPreviewRenderCounter(previewRenderCounter + 1)}>
                                <Refresh />
                            </IconButton>
                        </div>

                        {props.show && <Preview
                            previewRenderCounter={previewRenderCounter}
                            campaignId={props.comapaign?.id + ''}
                            labelNps={inputs.labelNps}
                            labelComment={inputs.labelComment}
                            labelThankYou={inputs.labelThankYou}
                            labelSubmit={inputs.labelSubmit}
                            logoUrl={inputs.logoUrl}
                        />}
                    </SidePanel.Field>
                </div>
                <SidePanel.Footer>
                    <div className='d-flex justify-between'>

                        <Button onClick={props.onClose} priority="secondary">
                            Close
                        </Button>

                        <div className='d-flex justify-end gap'>
                            {!isActive && <Button type='button' priority="secondary"
                                onClick={handleSaveAndActivate}
                            >
                                {processing ? <Loader size='tiny' /> : 'Save & Activate'}
                            </Button>}
                            <Button type={'submit'}
                                priority={'primary'}
                            >
                                {processing ? <Loader size='tiny' /> : 'Save'}
                            </Button>
                        </div>
                    </div>
                </SidePanel.Footer>
            </form>
        </SidePanel>
    </div>
}

export default CampaignSidePanel

